import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PatternComparison } from '@/services/PatternComparisonService';
import { TrendingUp, TrendingDown, Equal, ArrowRight, Plus, RefreshCw, Trash2 } from 'lucide-react';

interface PatternDetailModalProps {
  pattern: PatternComparison | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PatternDetailModal: React.FC<PatternDetailModalProps> = ({
  pattern,
  isOpen,
  onClose
}) => {
  if (!pattern) return null;

  const getStatusInfo = (status: string) => {
    const statusMap = {
      created: { 
        color: 'bg-green-500/10 text-green-600 border-green-500/20',
        icon: <Plus className="h-4 w-4" />,
        description: 'This pattern was newly created in the current analysis'
      },
      updated: { 
        color: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
        icon: <RefreshCw className="h-4 w-4" />,
        description: 'This pattern existed before and has been updated with new data'
      },
      deleted: { 
        color: 'bg-red-500/10 text-red-600 border-red-500/20',
        icon: <Trash2 className="h-4 w-4" />,
        description: 'This pattern existed before but was not found in the current analysis'
      },
      unchanged: { 
        color: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
        icon: <Equal className="h-4 w-4" />,
        description: 'This pattern remained exactly the same between analyses'
      }
    };

    return statusMap[status as keyof typeof statusMap] || statusMap.unchanged;
  };

  const formatPercentage = (value: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const formatChange = (value: number | null, isPercentage = false) => {
    if (value === null || value === undefined || value === 0) return '0';
    const sign = value > 0 ? '+' : '';
    const formatted = isPercentage ? `${value.toFixed(2)}%` : value.toFixed(2);
    return `${sign}${formatted}`;
  };

  const getChangeColor = (value: number | null) => {
    if (!value || value === 0) return 'text-gray-600';
    return value > 0 ? 'text-green-600' : 'text-red-600';
  };

  const getChangeIcon = (value: number | null) => {
    if (!value || value === 0) return <Equal className="h-4 w-4 text-gray-400" />;
    return value > 0 ? <TrendingUp className="h-4 w-4 text-green-500" /> : <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const statusInfo = getStatusInfo(pattern.status);

  // Calculate changes
  const frequencyChange = pattern?.changes?.frequency ? pattern.changes.frequency.after - pattern.changes.frequency.before : 0;
  
  const successRateChange = pattern?.changes?.success_rate ? pattern.changes.success_rate.after - pattern.changes.success_rate.before : 0;
  
  const confidenceChange = pattern?.changes?.confidence_score ? pattern.changes.confidence_score.after - pattern.changes.confidence_score.before : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {statusInfo.icon}
            Pattern Comparison Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge className={statusInfo.color}>
                  {statusInfo.icon}
                  <span className="ml-1 capitalize">{pattern.status}</span>
                </Badge>
                Change Status
              </CardTitle>
              <CardDescription>{statusInfo.description}</CardDescription>
            </CardHeader>
          </Card>

          {/* Pattern Content Comparison */}
          <Card>
            <CardHeader>
              <CardTitle>Pattern Content</CardTitle>
              <CardDescription>Pattern sequence information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Before (Backup)</h4>
                  <div className="p-3 bg-muted rounded-lg font-mono text-sm break-all">
                    {pattern.before?.pattern || 'N/A'}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">After (Current)</h4>
                  <div className="p-3 bg-muted rounded-lg font-mono text-sm break-all">
                    {pattern.after?.pattern || 'N/A'}
                  </div>
                </div>
              </div>
              {pattern.before?.pattern && pattern.after?.pattern && pattern.before.pattern !== pattern.after.pattern && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Pattern content has changed between analyses
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metrics Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Frequency Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Frequency Analysis
                  {getChangeIcon(frequencyChange)}
                </CardTitle>
                <CardDescription>How often this pattern appears</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Before:</span>
                    <span className="font-medium">{pattern.before?.frequency || 0}</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">After:</span>
                    <span className="font-medium">{pattern.after?.frequency || 0}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Change:</span>
                      <span className={`font-medium ${getChangeColor(frequencyChange)}`}>
                        {formatChange(frequencyChange)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Success Rate Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Success Rate Analysis
                  {getChangeIcon(successRateChange)}
                </CardTitle>
                <CardDescription>Pattern performance comparison</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Before:</span>
                    <span className="font-medium">{formatPercentage(pattern.before?.success_rate)}</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">After:</span>
                    <span className="font-medium">{formatPercentage(pattern.after?.success_rate)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Change:</span>
                      <span className={`font-medium ${getChangeColor(successRateChange)}`}>
                        {formatChange(successRateChange)}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Metrics */}
          {(pattern.before?.confidence_score !== null || pattern.after?.confidence_score !== null) && (
            <Card>
              <CardHeader>
                <CardTitle>Confidence Score Comparison</CardTitle>
                <CardDescription>Pattern detection confidence levels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-muted-foreground">Before</div>
                    <div className="text-lg font-medium">
                      {formatPercentage(pattern.before?.confidence_score)}
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">After</div>
                    <div className="text-lg font-medium">
                      {formatPercentage(pattern.after?.confidence_score)}
                    </div>
                  </div>
                </div>
                {confidenceChange !== 0 && (
                  <div className="mt-4 text-center">
                    <span className={`text-sm ${getChangeColor(confidenceChange)}`}>
                      Change: {formatChange(confidenceChange)}%
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pattern Impact Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Impact Analysis</CardTitle>
              <CardDescription>Overall assessment of pattern changes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {frequencyChange !== 0 && (
                  <div className="flex items-center gap-2">
                    {getChangeIcon(frequencyChange)}
                    <span className="text-sm">
                      Pattern frequency {frequencyChange > 0 ? 'increased' : 'decreased'} by{' '}
                      <strong>{Math.abs(frequencyChange)}</strong> occurrences
                    </span>
                  </div>
                )}
                {Math.abs(successRateChange) > 0.01 && (
                  <div className="flex items-center gap-2">
                    {getChangeIcon(successRateChange)}
                    <span className="text-sm">
                      Success rate {successRateChange > 0 ? 'improved' : 'declined'} by{' '}
                      <strong>{Math.abs(successRateChange).toFixed(2)}%</strong>
                    </span>
                  </div>
                )}
                {pattern.status === 'created' && (
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-green-500" />
                    <span className="text-sm">New pattern discovered with frequency {pattern.after?.frequency}</span>
                  </div>
                )}
            {pattern.status === 'deleted' && (
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-red-500" />
                    <span className="text-sm">Pattern no longer appears in current analysis</span>
                  </div>
                )}
            {pattern.status === 'unchanged' && (
                  <div className="flex items-center gap-2">
                    <Equal className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Pattern remained stable across analyses</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};